import { ReactNode, useMemo, useState } from "react";
import { motion } from "framer-motion";
import styles from "./nonCredly.module.css";
import {
	nonCredlyCerts,
	CATEGORY_ORDER,
	CertCategory,
	CertFile,
	CertTier,
	NonCredlyCert
} from "../../../../utils/nonCredlyCerts";
import { getYears, groupByYear } from "../../../../utils/timeline";
import { socialUrl } from "../../../../content";
import { containerVariants, cardVariants } from "./animations";
import CertificateModal from "./CertificateModal/CertificateModal";

/**
 * Derived from the cards, never hardcoded: a category with nothing behind it is
 * not offered at all. A dead pill invites a click that returns an empty section,
 * which reads as a broken filter rather than an empty category.
 */
const categories: Array<CertCategory | "All"> = [
	"All",
	...CATEGORY_ORDER.filter((cat: CertCategory) => nonCredlyCerts.some((c: NonCredlyCert) => c.category === cat))
];

/** From `profile.json` via the loader — never a literal (Article II.2). */
const CREDLY_PROFILE = socialUrl("credly");

const TIERS: Array<{ tier: CertTier; title: string; blurb: string }> = [
	{
		tier: "certification",
		title: "Accredited Certifications",
		blurb: "Credentials issued by accredited bodies, each independently verifiable."
	},
	{
		tier: "training",
		title: "Professional Training & Workshops",
		blurb: "Formal coursework and workshops completed alongside the credentials above."
	}
];

/* ============================== CARD ============================== */

/* The card is `card-shell` / `card-face` — the site-wide treatment (D-061).
   What it replaces was the only BORDERLESS card on the site: a flat
   `--veil-raised` fill with no edge and no elevation in dark, which read as a
   patch of lighter ground rather than as a card. The framer-motion reveal
   moves to the shell, so the transform and the hover lift stay on separate
   elements and cannot fight. */
const CertCard = ({ cert, onOpen }: { cert: NonCredlyCert; onOpen: (f: CertFile) => void }) => (
	<motion.div className="card-shell" variants={cardVariants}>
		<div className={`card-face ${styles.card}`}>
			<div className={styles.cardHead}>
				<h4>{cert.title}</h4>
				{cert.status && <span className={styles.status}>{cert.status}</span>}
			</div>

			<p className={styles.issuer}>{cert.issuer}</p>

			{cert.auditCritical && <span className={styles.audit}>Audit-Critical</span>}

			{/* One chip per certificate file. A credential with several files (e.g.
			    Green Belt and Black Belt) stays a single card carrying both, rather
			    than rendering as two indistinguishable cards. */}
			{cert.files.length > 0 && (
				<div className={styles.fileRow}>
					{cert.files.map((file: CertFile) => (
						<button
							key={`${cert.id}-${file.label}`}
							className={styles.fileChip}
							onClick={() => onOpen(file)}
							aria-label={`View ${file.label} certificate for ${cert.title}`}
						>
							{file.label}
						</button>
					))}
				</div>
			)}

			{cert.verifyLink && (
				<a
					className={styles.verify}
					href={cert.verifyLink}
					target="_blank"
					rel="noopener noreferrer"
					aria-label={`Verify ${cert.title} externally, opens in a new tab`}
				>
					Verify credential →
				</a>
			)}
		</div>
	</motion.div>
);

/* ========================== REVEAL GRID =========================== */

/**
 * The scroll reveal, and the one thing it must never be able to do: leave a
 * credential in the DOM at `opacity: 0`.
 *
 * This was `whileInView="visible"` on the grid itself, with `once: true`. It
 * was correct on first load and wrong on every filter click afterwards, because
 * **`whileInView` is a gesture, not a state**: it drives the node that is
 * intersecting, and it does not propagate to a child that mounts LATER. The
 * year rows are keyed by tier and year, so Management and AI both render
 * `Accredited Certifications-2026` and React REUSES that node — its `once`
 * observer has already fired and disconnected, the incoming card mounts having
 * inherited `hidden`, and nothing ever tells it to become visible. Clicking AI
 * after Management left `AI & Agility` at `opacity: 0` in the middle of the
 * viewport, under a tier count that said "1"; Security showed **0 of 4** the
 * same way, and "All" painted 5 of 22 (D-089).
 *
 * `animate` IS a state, and framer propagates it to children that mount after
 * the parent has settled. Latching it in `useState` keeps the visual identical
 * — one staggered fade the first time a row comes into view — while making
 * "this row is revealed" a fact about the row rather than a fact about an
 * observer that has since been thrown away.
 *
 * What this deliberately does NOT change: a row still below the fold stays
 * hidden until it is scrolled to. That is the reveal, it is the site's design
 * language (Services, Skills, Education_v2), and it heals the moment the reader
 * scrolls. The defect was rows that could never heal.
 */
const RevealGrid = ({ children }: { children: ReactNode }) => {
	const [revealed, setRevealed] = useState(false);

	return (
		<motion.div
			className={styles.grid}
			variants={containerVariants}
			initial="hidden"
			animate={revealed ? "visible" : "hidden"}
			onViewportEnter={() => setRevealed(true)}
			viewport={{ once: true, amount: 0.2 }}
		>
			{children}
		</motion.div>
	);
};

/* =========================== TIER BLOCK =========================== */

const TierBlock = ({
	title,
	blurb,
	certs,
	onOpen
}: {
	title: string;
	blurb: string;
	certs: NonCredlyCert[];
	onOpen: (f: CertFile) => void;
}) => {
	const years = useMemo(() => getYears(certs), [certs]);

	if (!certs.length) return null;

	return (
		<div className={styles.tier}>
			<div className={styles.tierHead}>
				<h3>{title}</h3>
				{/* The count joins the shared tinted-label vocabulary (D-061); it
				    was a neutral `--veil-quiet` pellet, the site's fourth tint
				    family for what is the same idea as an Education year pill. */}
				<span className="pill-tint border-[var(--cat-indigo-border)] bg-[var(--cat-indigo-fill)] tabular-nums text-[var(--cat-indigo-text)]">
					{certs.length}
				</span>
			</div>
			<p className={styles.tierBlurb}>{blurb}</p>

			{years.map((year: number) => (
				<div key={`${title}-${year}`} className={styles.timelineRow}>
					<div className={styles.year}>
						<span>{year}</span>
						<div className={styles.line} />
					</div>

					<RevealGrid>
						{groupByYear(certs, year).map((cert: NonCredlyCert) => (
							<CertCard key={cert.id} cert={cert} onOpen={onOpen} />
						))}
					</RevealGrid>
				</div>
			))}
		</div>
	);
};

/* ============================= SECTION ============================= */

export default function NonCredlyCertifications() {
	const [activeCategory, setCategory] = useState<CertCategory | "All">("All");
	const [selectedFile, setSelectedFile] = useState<CertFile | null>(null);

	const filtered = useMemo(() => {
		if (activeCategory === "All") return nonCredlyCerts;
		return nonCredlyCerts.filter((c: NonCredlyCert) => c.category === activeCategory);
	}, [activeCategory]);

	return (
		<section className="section-shell" id="certifications" aria-labelledby="certifications-heading">
			<div className={`section-rail ${styles.section}`}>
				{/* ================= HEADER ================= */}
				<header className={styles.header}>
					<div className={styles.headerInner}>
						{/* The eyebrow and the heading now use the site-wide rules
						    (D-061). The heading was 32px/600 where every other
						    section is 48px/700 — the outlier in both size and
						    weight — and the eyebrow was neutral white at 60% where
						    the shared tinted label is indigo. The WORDS are
						    untouched: which sections carry an eyebrow at all is a
						    content question and stays open. */}
						<p className="section-eyebrow">Verified Credentials</p>

						<h2 id="certifications-heading" className="heading">
							<span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--grad-brand-from)] via-[var(--grad-brand-via)] to-[var(--grad-brand-to)]">
								Certifications
							</span>{" "}
							&amp; Professional Development
						</h2>

						<p className="section-standfirst">
							Credentials verified against their issuing bodies — certificates and verification links open
							where available.
						</p>
					</div>

					{/* ================= FILTERS ================= */}
					<div className={styles.pills} role="group" aria-label="Filter credentials by category">
						{categories.map((cat: CertCategory | "All") => (
							<button
								key={cat}
								className={`${styles.pill} ${cat === activeCategory ? styles.active : ""}`}
								onClick={() => setCategory(cat)}
								aria-pressed={cat === activeCategory}
							>
								{cat}
							</button>
						))}
					</div>
				</header>

				{/* ================= TIERED TIMELINE ================= */}
				{TIERS.map(({ tier, title, blurb }) => (
					<TierBlock
						key={tier}
						title={title}
						blurb={blurb}
						certs={filtered.filter((c: NonCredlyCert) => c.tier === tier)}
						onOpen={setSelectedFile}
					/>
				))}

				{filtered.length === 0 && <p className={styles.empty}>No credentials in this category yet.</p>}

				{/* ================= CREDLY CTA ================= */}
				<footer className={styles.sectionFooter}>
					<a
						className={styles.credlyLink}
						href={CREDLY_PROFILE}
						target="_blank"
						rel="noopener noreferrer"
						aria-label="View full Credly profile, opens in a new tab"
					>
						View full Credly profile →
					</a>
					<p className={styles.trust}>Digitally verified badges · Employer-checkable</p>
				</footer>

				<CertificateModal open={!!selectedFile} file={selectedFile} onClose={() => setSelectedFile(null)} />
			</div>
		</section>
	);
}
